// §415(c) annual-additions schedule for a proposed profit-sharing allocation.
//
// Builds the per-person source schedule the worksheets and exports show:
// every annual-additions source represented in the normalized row (elective
// deferrals excluding catch-up, after-tax, safe-harbor and other nonelective,
// QNEC, match, reallocated forfeitures, supplied other-plan additions) plus
// the PROPOSED profit sharing. Catch-up contributions are never annual
// additions. Source priority is explicit: existing sources are facts and are
// never reduced; the proposed profit sharing is the only adjustable source,
// so any excess reduces it — requested, reduced, and final amounts are all
// preserved. All math in integer cents.

import { makeComplianceStatus, makeReason } from './complianceStatus.js'

// Reduction order is a property of the schedule, not of the caller.
export const SECTION_415C_SOURCE_ORDER = Object.freeze([
  'deferral', 'afterTax', 'safeHarborNonelective', 'otherNonelective',
  'qnec', 'match', 'forfeiture', 'otherPlan', 'profitSharing',
])

export function buildSection415Schedule(participants = [], {
  proposedProfitSharingCentsById = {},
} = {}) {
  const rows = []
  const overCapIds = []
  let totalReducedCents = 0
  for (const person of Array.isArray(participants) ? participants : []) {
    if (!person) continue
    const capCents = Math.max(0, Math.min(
      person.limit415cCents || 0,
      Math.round((Number.isFinite(Number(person.section415Compensation))
        ? Number(person.section415Compensation)
        : Number(person.allocationCompensation) || 0) * 100),
    ))
    const fixed = {
      deferral: person.deferralCents || 0,
      afterTax: person.afterTaxCents || 0,
      safeHarborNonelective: person.safeHarborNonelectiveCents || 0,
      otherNonelective: person.otherNonelectiveCents || 0,
      qnec: person.qnecCents || 0,
      match: person.matchCents || 0,
      forfeiture: person.forfeitureCents || 0,
      otherPlan: person.otherPlanAnnualAdditionsCents || 0,
    }
    const fixedTotalCents = Object.values(fixed).reduce((sum, value) => sum + value, 0)
    const headroomCents = Math.max(0, capCents - fixedTotalCents)
    const requestedCents = Math.max(0, Math.round(Number(proposedProfitSharingCentsById[person.id]) || 0))
    const finalCents = Math.min(requestedCents, headroomCents)
    const reducedCents = requestedCents - finalCents
    totalReducedCents += reducedCents
    const existingOverCapCents = Math.max(0, fixedTotalCents - capCents)
    if (existingOverCapCents > 0) overCapIds.push(person.id)
    rows.push({
      id: person.id,
      capCents,
      sources: SECTION_415C_SOURCE_ORDER.map((source) => ({
        source,
        requestedCents: source === 'profitSharing' ? requestedCents : fixed[source],
        reducedCents: source === 'profitSharing' ? reducedCents : 0,
        finalCents: source === 'profitSharing' ? finalCents : fixed[source],
      })),
      fixedTotalCents,
      headroomBeforeCents: headroomCents,
      profitSharingRequestedCents: requestedCents,
      profitSharingReducedCents: reducedCents,
      profitSharingFinalCents: finalCents,
      totalAdditionsCents: fixedTotalCents + finalCents,
      remainingHeadroomCents: Math.max(0, capCents - fixedTotalCents - finalCents),
      atLimit: fixedTotalCents + finalCents >= capCents,
      existingOverCapCents,
    })
  }
  return {
    ...makeComplianceStatus({
      status: overCapIds.length ? 'fails' : 'passes',
      reasons: overCapIds.length
        ? [makeReason('existing-additions-exceed-415c',
            'Annual additions already entered on the census exceed the §415(c) limit before any proposed profit sharing.',
            { employeeIds: overCapIds })]
        : [],
    }),
    rows,
    overCapIds,
    totalReducedCents,
    anyReduced: totalReducedCents > 0,
  }
}
